import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { 
  RobustnessScore, 
  RobustnessData, 
  RobustnessAdapter,
  RobustnessScoreApiPayload
} from '../../models/robustness'; 
import { RobustnessService } from '../../services/robustness.service'; 
import { catchError, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { ITeamRobustnessScoreAnalysis } from '../../_interfaces/teamRobustnessScoreAnalysis';
import { MarkdownModule } from 'ngx-markdown';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Team } from '../../models/team';


// Interface pour structurer les données d'affichage des barres
interface SubScoreDisplay {
  name: string;
  score: number;
  colorClass: string;
}

// Interface for the checklist
interface ChecklistItem {
  textKey: string;
  isCompleted: boolean;
}

@Component({
  selector: 'app-team-score',
  standalone: true,
  imports: [CommonModule, MarkdownModule, TranslateModule], 
  templateUrl: './team-score.component.html',
  styleUrl: './team-score.component.scss'
})
export class TeamScoreComponent implements OnChanges {

  @Input() team!: Team; 
  @Output() requestTeamEdit = new EventEmitter<void>();
  @Output() requestAssignment = new EventEmitter<void>();

  // tempalte properties
  latestScore: RobustnessScore | null = null;
  overallScoreColor: string = 'text-secondary';
  subScoresDisplay: SubScoreDisplay[] = [];
  isLoading: boolean = false;
  
  // Checklist properties
  checklist: ChecklistItem[] = [];
  showChecklist: boolean = false;
  nextStepTextKey: string = '';
  
  // --- AI Analysis properties---
  showAiAnalysisModal: boolean = false;
  loadingAnalysis: boolean = false;
  aiAnalysisText: string | null = null; 

  // Rendre la méthode statique du modèle disponible dans le template
  getColorClassStatic = RobustnessScore.getColorClassForScore; 

  constructor(
    private robustnessService: RobustnessService,
    private robustnessAdapter: RobustnessAdapter,
    private translateService: TranslateService 
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['team']?.currentValue) {
      this.updateChecklist();
  
      const canCalculate = this.checklist.every(item => item.isCompleted);
      this.showChecklist = !canCalculate;
  
      if (canCalculate) {
          // On first load, just try to load. On subsequent changes, force recalculation.
          if (changes['team'].isFirstChange()) {
              this.loadData(this.team.id);
          } else {
              this.forceRecalculate(this.team.id);
          }
      } else {
        this.resetScore();
      }
    }
  }

  private updateChecklist(): void {
    this.checklist = [
      { textKey: 'TEAM.SCORE.CHECKLIST.ROLES', isCompleted: (this.team?.roles?.length || 0) > 0 },
      { textKey: 'TEAM.SCORE.CHECKLIST.SKILLS', isCompleted: (this.team?.skills?.length || 0) > 0 },
      { textKey: 'TEAM.SCORE.CHECKLIST.MEMBERS', isCompleted: (this.team?.peoples?.length || 0) > 0 },
    ];

    const nextStep = this.checklist.find(item => !item.isCompleted);
    if (nextStep) {
      if (nextStep.textKey === 'TEAM.SCORE.CHECKLIST.ROLES') {
        this.nextStepTextKey = 'TEAM.SCORE.CHECKLIST.NEXT_STEP_ROLES';
      } else if (nextStep.textKey === 'TEAM.SCORE.CHECKLIST.SKILLS') {
        this.nextStepTextKey = 'TEAM.SCORE.CHECKLIST.NEXT_STEP_SKILLS';
      } else {
        this.nextStepTextKey = 'TEAM.SCORE.CHECKLIST.NEXT_STEP_MEMBERS';
      }
    } else {
      this.nextStepTextKey = ''; // All steps are complete
    }
  }

  private resetScore(): void {
    this.latestScore = null;
    this.subScoresDisplay = [];
    this.isLoading = false;
  }

  public forceRecalculate(teamId: number): void {
    if (!teamId || this.isLoading) return;

    this.isLoading = true;
    this.latestScore = null;

    this.robustnessService.calculateTeamRobustnessScore(teamId)
      .pipe(
        take(1),
        switchMap(() => {
          console.log('IRO calculation successful. Reloading score...');
          return this.robustnessService.getTeamRobustnessScore(teamId).pipe(take(1));
        }),
        catchError((err) => {
          console.error('Error during force recalculation or second GET:', err);
          this.isLoading = false;
          return throwError(() => err);
        })
      )
      .subscribe({
        next: (apiData: RobustnessScoreApiPayload) => {
          const adaptedData: RobustnessData = this.robustnessAdapter.adapt(apiData);
          this.latestScore = adaptedData.latest;
          if (this.latestScore) {
            this.initializeDisplayData();
          }
          this.isLoading = false;
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error loading IRO after force recalculation:', err);
          this.isLoading = false;
          this.latestScore = null;
        }
      });
  }

  /**
   * Fetch the team robustness score from the API using the given team ID.
   * If the team ID is not set, it will do nothing.
   * If the score is not found (404), it will attempt to calculate it using a POST request.
   * If the POST or second GET fails, it will stop the loading and set the latest score to null.
   * @param id the team ID to fetch the score for
   */
  private loadData(id: number): void {
    if (this.isLoading || !id) return;

    this.isLoading = true;
    this.latestScore = null;
    
    // 1. Appel du service qui retourne le payload JSON brut
    this.robustnessService.getTeamRobustnessScore(id)
      .pipe(take(1),
      catchError((err: HttpErrorResponse) => {
        
        // **2. Interception du 404 : Score inexistant**
        if (err.status === 404) {
              console.warn('IRO score not found (404). Attempting calculation...');

              // Basculer sur l'Observable de l'appel POST pour le calcul
              return this.robustnessService.calculateTeamRobustnessScore(id)
                .pipe(
                  take(1),
                  // **3. switchMap : Après le succès du POST, relancer le GET**
                  switchMap(() => {
                    console.log('IRO calculation successful. Reloading score...');
                    return this.robustnessService.getTeamRobustnessScore(id).pipe(take(1));
                  }),
                  // Gérer l'erreur si le POST ou le second GET échoue
                  catchError((postErr) => {
                    console.error('Error during POST calculation or second GET:', postErr);
                    return throwError(() => postErr);
                  })
                );
        }
        
        // Si ce n'est pas un 404 (ex: 500 ou erreur réseau), on propage l'erreur immédiatement
        console.error(`Error loading IRO (Status ${err.status}).`);
        return throwError(() => err); 
      })
      )
      .subscribe({
        next: (apiData: RobustnessScoreApiPayload) => {
          const adaptedData: RobustnessData = this.robustnessAdapter.adapt(apiData);

          this.latestScore = adaptedData.latest;
          
          if (this.latestScore) {
              this.initializeDisplayData();
          }
          this.isLoading = false;
        },
        error: (err:HttpErrorResponse ) => {
          console.error('Error loading IRO:', err);
          this.isLoading = false;
          this.latestScore = null; 
        }
      });
  }

  fetchAiAnalysis(): void {
        if (this.loadingAnalysis || !this.team.id) return;
        
        this.loadingAnalysis = true;
        this.aiAnalysisText = null; 

        this.robustnessService.getTeamRobustnessScoreAiAnalysis(this.team.id)
            .pipe(take(1))
            .subscribe({
                next: (response: ITeamRobustnessScoreAnalysis) => {
                    this.aiAnalysisText = response.analysis 
                        ? response.analysis
                        : 'Aucune analyse détaillée n\'est disponible.';
                        
                    this.loadingAnalysis = false;
                },
                error: (err: HttpErrorResponse) => {
                    this.translateService.get('ERROR.AI_ANALYSIS_FAILED').subscribe((translatedText: string) => {
                    this.aiAnalysisText = `<p class="text-danger">${translatedText}</p>`;
                    this.loadingAnalysis = false;
                });
                }
            });
    }

  /**
   * Prépare le tableau des sous-scores pour l'affichage (couleurs et noms).
   */
  private initializeDisplayData(): void {
    if (!this.latestScore) return;

    // Détermination de la couleur du score global via la méthode statique
    this.overallScoreColor = RobustnessScore.getColorClassForScore(this.latestScore.score, 'text');
    
    const scores = this.latestScore.subScores;
    this.subScoresDisplay = [
      { name: 'Compétences', score: scores.skills, colorClass: RobustnessScore.getColorClassForScore(scores.skills, 'progress') },
      { name: 'Staffing', score: scores.staffing, colorClass: RobustnessScore.getColorClassForScore(scores.staffing, 'progress') },
      { name: 'Allocation & Charge', score: scores.allocation, colorClass: RobustnessScore.getColorClassForScore(scores.allocation, 'progress') },
    ].sort((a, b) => b.score - a.score);
  }

  openAiAnalysisModal(): void {
        this.showAiAnalysisModal = true;
        this.fetchAiAnalysis();
    }

    closeAiAnalysisModal(): void {
        this.showAiAnalysisModal = false;
    }

  onGetStartedClick(): void {
    if (this.nextStepTextKey === 'TEAM.SCORE.CHECKLIST.NEXT_STEP_MEMBERS') {
      this.requestAssignment.emit();
    } else {
      this.requestTeamEdit.emit();
    }
  }
}